import { useCallback } from 'react';
import { trpc } from '@client-trpc';
import { useNotifications } from '@client/foundation/notifications/notifications.hook';
import type { TemplateType } from '@server/trading-templates/schemas/input.schema';
import type { TradingTemplate } from '@server/trading-templates/schemas/output.schema';

type OpenTemplateEditorArgs = {
  templateId?: string;
  templateName: string;
  initialConfig: TradingTemplate['config'];
  onSaved?: (savedTemplate: {
    templateId: string;
    name: string;
    config: TradingTemplate['config'];
  }) => void;
};

type UseSharedTemplateActionsArgs = {
  templateType: TemplateType;
  openTemplateEditor: (args: OpenTemplateEditorArgs) => void;
};

export const useSharedTemplateActions = ({
  templateType,
  openTemplateEditor
}: UseSharedTemplateActionsArgs) => {
  const trpcClient = trpc.useUtils().client;
  const utils = trpc.useUtils();
  const { notify } = useNotifications();
  const { mutateAsync: createSharedResource } =
    trpc.sharedResources.createSharedResource.useMutation();
  const { mutateAsync: importSharedResource } =
    trpc.sharedResources.importSharedResource.useMutation();
  const { mutateAsync: updateSharedResource } =
    trpc.sharedResources.updateSharedResource.useMutation();
  const { mutateAsync: revokeSharedResource } =
    trpc.sharedResources.revokeSharedResource.useMutation();

  const invalidateSharedTemplateQueries = useCallback(async () => {
    await Promise.all([
      utils.tradingTemplates.tradingTemplates.invalidate({ templateType }),
      utils.sharedResources.listSharedResources.invalidate({
        resourceType: 'template',
        templateType
      })
    ]);
  }, [templateType, utils]);

  const handleShare = useCallback(
    async (templateId: string) => {
      try {
        await createSharedResource({
          resourceType: 'template',
          templateId
        });

        await utils.sharedResources.listSharedResources.invalidate({
          resourceType: 'template',
          templateType
        });

        notify({
          type: 'success',
          message: 'Template published to Shared'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error ? error.message : 'Failed to share template'
        });
      }
    },
    [createSharedResource, notify, templateType, utils.sharedResources.listSharedResources]
  );

  const handleImportShared = useCallback(
    async (sharedResourceId: string, sharedResourceName: string) => {
      try {
        await importSharedResource({
          sharedResourceId,
          name: sharedResourceName
        });

        await invalidateSharedTemplateQueries();

        notify({
          type: 'success',
          message: 'Shared template imported'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to import shared template'
        });
      }
    },
    [importSharedResource, invalidateSharedTemplateQueries, notify]
  );

  const handleModifyShared = useCallback(
    async (sharedResourceId: string) => {
      try {
        const sharedResource =
          await trpcClient.sharedResources.getSharedResource.query({
            sharedResourceId
          });

        if (sharedResource.payload.resourceType !== 'template') {
          throw new Error('Shared resource is not a template');
        }

        openTemplateEditor({
          templateName: sharedResource.payload.rootName,
          initialConfig: sharedResource.payload.config,
          onSaved: async ({ name, config }) => {
            await updateSharedResource({
              resourceType: 'template',
              sharedResourceId,
              name,
              config
            });

            await invalidateSharedTemplateQueries();

            notify({
              type: 'success',
              message: 'Shared template updated successfully'
            });
          }
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to prepare shared template update'
        });
      }
    },
    [
      invalidateSharedTemplateQueries,
      notify,
      openTemplateEditor,
      trpcClient,
      updateSharedResource
    ]
  );

  const handleRevokeShared = useCallback(
    async (sharedResourceId: string) => {
      try {
        await revokeSharedResource({ sharedResourceId });
        await utils.sharedResources.listSharedResources.invalidate({
          resourceType: 'template',
          templateType
        });

        notify({
          type: 'success',
          message: 'Shared template revoked'
        });
      } catch (error) {
        notify({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'Failed to revoke shared template'
        });
      }
    },
    [
      notify,
      revokeSharedResource,
      templateType,
      utils.sharedResources.listSharedResources
    ]
  );

  return {
    handleShare,
    handleImportShared,
    handleModifyShared,
    handleRevokeShared
  };
};
